import type { Chain } from 'viem';
import type {
  Competition,
  Context,
  IncentiveToken,
  InstanceID,
  ProtocolInstance,
  RewardsConfig,
  Token,
  TokenID,
} from '@/api/spec.ts';
import { environment } from '../environment.ts';

export interface AppContext {
  chain: Partial<Chain> & { rpcUrlsList?: string[] };
  contracts: Map<InstanceID, ProtocolInstance>;
  tokens: Map<TokenID, Token>;
  geoBlock?: string[];
  userCountry?: string;
  userRegion?: string;
  isGeoBlocked: boolean;
  rewards?: RewardsConfig;
  competitions?: Competition[];
  incentiveTokens?: IncentiveToken[];
  features: {
    languageSelection: boolean;
    pictureInPictureEnabled: boolean;
    collectUsersFeedback: boolean;
    referralsEnabled: boolean;
    referralDashboardEnabled: boolean;
    pointsEnabled: boolean;
    showRealPoints: boolean;
    rewardsEnabled: boolean;
    tournamentEnabled: boolean;
    apiKeysEnabled: boolean;
    // Gates the changelog page (nav + route). Content is a client-side mock until the API ships.
    changelogEnabled: boolean;
    // Gates the funding rates page (nav + route).
    fundingRatesEnabled: boolean;
    duneDashboardUrl: string;
    funBridgeEnabled: boolean;
    customSlippageEnabled: boolean;
    feeTiersEnabled: boolean;
    // Market order surfaces a fill-prognosis warning when the estimated price impact
    // (avg fill vs best price) crosses this percentage. Configurable so we can tune per-env.
    marketOrderImpactWarnPct: number;
    mainLogoLabel?: string;
    globalAnnouncement?: string;
    docsUrl: string;
    twitterUrl: string;
    discordUrl: string;
    waitlistUrl: string;
    termsOfUseUrl: string;
    privacyPolicyUrl: string;
    analyticsEnabled: boolean;
    rescueEnabled: boolean;
    showLeverageTooltip: boolean;
    // Trigger TP/SL & stop orders on mark price (GTEMark/LTEMark) instead of last (GTELast/LTELast).
    tpSlTriggerOnMarkPrice: boolean;
    // Submit TP/SL orders as IoC (market-like) instead of GtC (limit-like).
    tpSlOrdersUseIoc: boolean;
    // Instant the liquidation distribution switched from even (1/3 each) to 80/10/10.
    // Unix epoch seconds as shipped by the backend (e.g. "1781817842"); ISO-8601 also accepted.
    liquidationSplitChangeAt?: string;
    // TEMPORARY: gates the CA/LY/PA pre-block notice (A-2224); the key predates Canada-wide. Remove with the hook.
    ontarioBanAnnouncementEnabled: boolean;
    showMillionBanner: boolean;
    makerBoostEnabled: boolean;
    // Gates the Builder Codes page (nav + route) and the Funding Rates terminal tab together.
    showBuildersFundingHistory: boolean;
    builderApplyUrl: string;
    animatedFooterTicker: boolean;
    flipPositionEnabled: boolean;
  };
}

export const mapContext = (apiContext: Context & { userCountry?: string; userRegion?: string }): AppContext | undefined => {
  if (!apiContext?.chain) {
    return undefined;
  }

  const userCountry = apiContext.userCountry?.toLowerCase();
  const userRegion = userCountry && apiContext.userRegion ? `${userCountry}-${apiContext.userRegion.toLowerCase()}` : undefined;

  return {
    chain: {
      id: apiContext.chain.chain_id,
      name: apiContext.chain.name,
      nativeCurrency: apiContext.chain.native_token
        ? {
            name: apiContext.chain.native_token.name,
            symbol: apiContext.chain.native_token.symbol,
            decimals: apiContext.chain.native_token.decimals,
          }
        : undefined,
      rpcUrls: apiContext.chain.rpc_urls
        ? {
            default: {
              http: apiContext.chain.rpc_urls,
            },
            public: {
              http: apiContext.chain.rpc_urls,
            },
          }
        : undefined,
      rpcUrlsList: apiContext.chain.rpc_urls,
      blockExplorers: apiContext.chain.block_explorer_urls?.length
        ? {
            default: {
              name: `${apiContext.chain.name} Explorer`,
              url: apiContext.chain.block_explorer_urls[0],
            },
          }
        : undefined,
    },
    contracts: apiContext.instances.reduce((acc: Map<InstanceID, ProtocolInstance>, cur) => {
      acc.set(cur.id, cur);
      return acc;
    }, new Map<InstanceID, ProtocolInstance>()),
    tokens: apiContext.tokens.reduce((acc: Map<TokenID, Token>, cur) => {
      cur?.id != null && acc.set(cur.id, cur);
      return acc;
    }, new Map<TokenID, Token>()),
    geoBlock: apiContext.geo_block,
    userCountry,
    userRegion,
    isGeoBlocked: !!apiContext.geo_block?.some(
      (code) => code.toLowerCase() === userCountry || code.toLowerCase() === userRegion
    ),
    rewards: apiContext.rewards,
    competitions: apiContext.competitions,
    incentiveTokens: apiContext.incentive_tokens,
    features: {
      languageSelection: apiContext.features?.languageSelection === 'on',
      pictureInPictureEnabled: apiContext.features?.pictureInPictureEnabled === 'on' || !environment.production,
      collectUsersFeedback: apiContext.features?.collectUsersFeedback === 'on' || !environment.production,
      referralsEnabled: apiContext.features?.referralsEnabled === 'on',
      referralDashboardEnabled: apiContext.features?.referralDashboardEnabled === 'on' || !environment.production,
      pointsEnabled: apiContext.features?.pointsEnabled === 'on' || !environment.production,
      showRealPoints: apiContext.features?.showRealPoints === 'on',
      rewardsEnabled: apiContext.features?.rewardsEnabled === 'on' || !environment.production,
      tournamentEnabled: apiContext.features?.tournamentEnabled === 'on' || !environment.production,
      apiKeysEnabled: apiContext.features?.apiKeysEnabled === 'on' || !environment.production,
      changelogEnabled: apiContext.features?.changelogEnabled === 'on' || !environment.production,
      fundingRatesEnabled: apiContext.features?.fundingRatesEnabled === 'on' || !environment.production,
      duneDashboardUrl:
        apiContext.features?.duneDashboardUrl || (environment.production ? '' : 'https://dune.com/perpl/perpl-dex'),
      funBridgeEnabled: apiContext.features?.funBridgeEnabled === 'on' && environment.production,
      customSlippageEnabled: apiContext.features?.customSlippageEnabled === 'on' || !environment.production,
      feeTiersEnabled: apiContext.features?.feeTiersEnabled === 'on' || !environment.production,
      // Falls through to 0 (warning disabled) when the API doesn't ship a value or it's non-numeric.
      // The fill-prognosis component treats 0/missing as "feature off".
      marketOrderImpactWarnPct: Number(apiContext.features?.marketOrderImpactWarnPct) || 0,
      mainLogoLabel: apiContext.features?.mainLogoLabel,
      globalAnnouncement: apiContext.features?.globalAnnouncement,
      docsUrl: apiContext.features?.docsUrl || '',
      twitterUrl: apiContext.features?.twitterUrl || '',
      discordUrl: apiContext.features?.discordUrl || '',
      termsOfUseUrl: apiContext.features?.termsOfUseUrl || '',
      privacyPolicyUrl: apiContext.features?.privacyPolicyUrl || '',
      waitlistUrl: apiContext.features?.waitlistUrl || '',
      analyticsEnabled: apiContext.features?.analyticsEnabled !== 'off',
      rescueEnabled: apiContext.features?.rescueEnabled === 'on',
      showLeverageTooltip: apiContext.features?.showLeverageTooltip === 'on' || !environment.production,
      tpSlTriggerOnMarkPrice: apiContext.features?.tpSlTriggerOnMarkPrice === 'on',
      tpSlOrdersUseIoc: apiContext.features?.tpSlOrdersUseIoc === 'on',
      liquidationSplitChangeAt: apiContext.features?.liquidationSplitChangeAt || '1781817842',
      ontarioBanAnnouncementEnabled: apiContext.features?.ontarioBanAnnouncementEnabled === 'on',
      showMillionBanner: apiContext.features?.showMillionBanner === 'on',
      makerBoostEnabled: apiContext.features?.makerBoostEnabled === 'on',
      showBuildersFundingHistory: apiContext.features?.showBuildersFundingHistory === 'on',
      builderApplyUrl: apiContext.features?.builderApplyUrl || '',
      animatedFooterTicker: apiContext.features?.animatedFooterTicker !== 'off',
      flipPositionEnabled: apiContext.features?.flipPositionEnabled === 'on' || !environment.production,
    },
  };
};
